import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';

const courses = [
  {
    id: 'stock-market-101',
    title: 'Stock Market 101',
    price: 499,
    description: 'Master the fundamentals of stock trading. Learn technical analysis, chart patterns, and risk management.',
    image: 'https://images.unsplash.com/photo-1745509267699-1b1db256601e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBzdG9jayUyMG1hcmtldCUyMGNoYXJ0cyUyMGRhcmt8ZW58MHx8fHwxNzc1MzI4OTQyfDA&ixlib=rb-4.1.0&q=85',
    level: 'Beginner'
  },
  {
    id: 'options-mastery',
    title: 'Options Mastery',
    price: 599,
    description: 'Deep dive into options trading strategies. Learn calls, puts, spreads, and hedging techniques.',
    image: 'https://images.unsplash.com/photo-1643962577481-4ff81600e439?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxmaW5hbmNpYWwlMjBzdG9jayUyMG1hcmtldCUyMGNoYXJ0cyUyMGRhcmt8ZW58MHx8fHwxNzc1MzI4OTQyfDA&ixlib=rb-4.1.0&q=85',
    level: 'Intermediate'
  },
  {
    id: 'futures-pro',
    title: 'Futures Pro',
    price: 999.99,
    description: 'Professional futures trading. Master commodity futures, index futures, and leverage management.',
    image: 'https://images.unsplash.com/photo-1640458240527-6f27eabe8368?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxmaW5hbmNpYWwlMjBzdG9jayUyMG1hcmtldCUyMGNoYXJ0cyUyMGRhcmt8ZW58MHx8fHwxNzc1MzI4OTQyfDA&ixlib=rb-4.1.0&q=85',
    level: 'Advanced',
    featured: true
  }
];

export function CoursesSection() {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();

  return (
    <section id="courses" className="py-24 bg-[#09090B] grid-lines">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('landing.premiumCourses')}
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            {t('landing.premiumCoursesDesc')}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course, index) => (
            <div 
              key={course.id}
              className={`card-glow bg-[#141417] border rounded-md overflow-hidden animate-fade-in-up stagger-${index + 1} ${course.featured ? 'card-featured border-[#007AFF]/50' : 'border-[#27272A]'}`}
              data-testid={`course-card-${course.id}`}
            >
              {course.featured && (
                <div className="bg-[#007AFF] text-white text-xs font-bold uppercase tracking-wider py-1 px-4 text-center">
                  {t('landing.mostPopular')}
                </div>
              )}
              <div className="relative h-48 overflow-hidden">
                <img src={course.image} alt={course.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#141417] to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <span className="bg-[#27272A] text-[#A1A1AA] text-xs px-2 py-1 rounded-sm">{course.level}</span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{course.title}</h3>
                <p className="text-[#A1A1AA] text-sm mb-4">{course.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-white font-mono">${course.price.toLocaleString()}</span>
                  <Link to={isAuthenticated ? `/checkout/${course.id}` : '/register'}>
                    <Button className="btn-primary text-sm" data-testid={`buy-course-${course.id}-btn`}>
                      {t('landing.enrollNow')}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Live Trading Signals Card */}
        <div className="mt-8 bg-gradient-to-r from-[#007AFF]/10 to-transparent border border-[#007AFF]/30 rounded-md p-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 bg-[#00FF66] rounded-full animate-pulse"></span>
                <span className="text-[#00FF66] text-sm font-medium">{t('landing.liveSignals')}</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {t('landing.liveSignalsTitle')}
              </h3>
              <p className="text-[#A1A1AA]">{t('landing.liveSignalsDesc')}</p>
            </div>
            <div className="text-center md:text-right">
              <div className="text-3xl font-bold text-white font-mono mb-2">
                $199<span className="text-lg text-[#71717A]">{t('landing.perWeek')}</span>
              </div>
              <Link to={isAuthenticated ? '/checkout/live-trading-signals' : '/register'}>
                <Button className="btn-primary" data-testid="buy-signals-btn">{t('landing.subscribeNow')}</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
